import type { KbDoc, SearchOutcome } from "./engine";
import type { Intent } from "./intent";
import { HELP_FLOWS, type HelpFlow } from "@/content/help/flows";
import { normalize } from "./normalize";

/**
 * Composição da resposta a partir de templates fixos.
 *
 * A regra de ouro da operação vive aqui: **toda palavra exibida vem de um
 * documento da base ou de um template deste arquivo**. Nada é gerado, nada é
 * resumido — resumir é gerar. Se um documento ficou longo demais para ler, o
 * problema está no texto da base e se corrige lá.
 */

export interface AnswerBlock {
  kind: "answer";
  title: string;
  /** Corpo do documento, ÍNTEGRO. */
  body: string;
  /** Dica destacada, quando o documento tem uma. */
  tip?: string;
  sourceScreen: string;
  /** Rota para "Abrir a tela", só quando a rota existe. */
  route: string | null;
  docId: string;
  /** Chips de aprofundamento. */
  chips: Chip[];
}

export interface DisambiguationBlock {
  kind: "disambiguation";
  prompt: string;
  options: Array<{ docId: string; label: string; screen: string }>;
}

export interface UnknownBlock {
  kind: "unknown";
  message: string;
  nearest: Array<{ docId: string; label: string; screen: string }>;
  supportPrompt: string;
}

export interface StartBlock {
  kind: "start";
  message: string;
  suggestions: Array<{ docId: string; label: string; screen: string }>;
}

export interface FlowBlock {
  kind: "flow";
  flowId: string;
  title: string;
  stepIndex: number;
  totalSteps: number;
  text: string;
  route?: string;
  docId?: string;
}

export type ComposedResponse =
  | AnswerBlock
  | DisambiguationBlock
  | UnknownBlock
  | StartBlock
  | FlowBlock;

export interface Chip {
  label: string;
  /** `doc` abre outro documento; `flow` inicia um fluxo; `feedback` sinaliza que não resolveu. */
  action: "doc" | "flow" | "feedback";
  target?: string;
}

/** Templates fixos. Nenhum texto do assistente nasce fora daqui. */
export const TEMPLATES = {
  deepen: "Quer que eu detalhe?",
  notSolved: "Não resolveu",
  whyChip: "Quer entender por que o sistema exige isso?",
  disambiguation: "Você quer dizer…",
  unknown:
    "Não encontrei isso na ajuda do sistema — e prefiro dizer que não sei a arriscar uma " +
    "resposta errada.",
  unknownNearest: "Talvez seja um destes:",
  support: "Se não for, fale com o suporte — eu levo junto a tela em que você está e o que você perguntou.",
  start: "Sobre o que você quer saber?",
  openScreen: "Abrir a tela",
  flowNext: "Concluí, e agora?",
  flowDone: "Pronto, o fluxo terminou.",
  planMissing: "Este recurso não está disponível no seu plano.",
} as const;

/** O corpo dos documentos guarda a dica separada por linha em branco dupla. */
function splitTipFromBody(text: string): { body: string; tip?: string } {
  const partes = text.split("\n\n");
  if (partes.length < 2) return { body: text };
  return { body: partes.slice(0, -1).join("\n\n"), tip: partes[partes.length - 1] };
}

function shortLabel(doc: KbDoc): string {
  // O título é "<Tela> — <Seção>"; o chip mostra só a seção.
  const traço = doc.title.indexOf(" — ");
  return traço > 0 ? doc.title.slice(traço + 3) : doc.title;
}

function toOption(doc: KbDoc) {
  return { docId: doc.id, label: shortLabel(doc), screen: doc.screen };
}

/**
 * Chips de aprofundamento.
 *
 * Vêm de `related` e da mesma tela: é assim que a pessoa faz a pergunta certa
 * sem saber que precisava fazê-la. Ao entregar um `how`, o `why` correspondente
 * é oferecido explicitamente — é esse par que transforma o assistente de manual
 * em professor.
 */
export function buildChips(doc: KbDoc, todos: Map<string, KbDoc>, intent: Intent): Chip[] {
  const chips: Chip[] = [];
  const relacionados = (doc.related ?? []).map((id) => todos.get(id)).filter((d): d is KbDoc => !!d);

  if (intent === "how" || doc.kind === "how") {
    const porque = relacionados.find((d) => d.kind === "why" || d.kind === "rule");
    if (porque) chips.push({ label: TEMPLATES.whyChip, action: "doc", target: porque.id });
  }

  for (const rel of relacionados) {
    if (chips.length >= 3) break;
    if (chips.some((c) => c.target === rel.id)) continue;
    chips.push({ label: shortLabel(rel), action: "doc", target: rel.id });
  }

  chips.push({ label: TEMPLATES.notSolved, action: "feedback" });
  return chips;
}

export function composeAnswer(doc: KbDoc, todos: Map<string, KbDoc>, intent: Intent): AnswerBlock {
  const { body, tip } = splitTipFromBody(doc.text);
  return {
    kind: "answer",
    title: shortLabel(doc),
    // Íntegro. Nunca resumido — resumir é gerar.
    body,
    tip,
    sourceScreen: doc.screen,
    route: doc.route,
    docId: doc.id,
    chips: buildChips(doc, todos, intent),
  };
}

export function composeOutcome(
  outcome: SearchOutcome,
  todos: Map<string, KbDoc>,
  intent: Intent,
): ComposedResponse {
  switch (outcome.kind) {
    case "answer":
      return composeAnswer(outcome.doc, todos, intent);
    case "disambiguation":
      return {
        kind: "disambiguation",
        prompt: TEMPLATES.disambiguation,
        options: outcome.options.slice(0, 3).map(toOption),
      };
    case "unknown":
      return {
        kind: "unknown",
        message: TEMPLATES.unknown,
        nearest: outcome.nearest.slice(0, 3).map(toOption),
        supportPrompt: TEMPLATES.support,
      };
    case "start":
      return {
        kind: "start",
        message: TEMPLATES.start,
        suggestions: outcome.suggestions.slice(0, 5).map(toOption),
      };
  }
}

/**
 * Um fluxo casa quando a pergunta traz termos suficientes dele. Limiar alto de
 * propósito: um fluxo sequestra a conversa por vários passos, e disparar por
 * engano é pior que não disparar.
 */
export function matchFlow(pergunta: string): HelpFlow | undefined {
  const tokens = new Set(normalize(pergunta));
  if (tokens.size === 0) return undefined;

  let melhor: { flow: HelpFlow; hits: number } | undefined;
  for (const flow of HELP_FLOWS) {
    const alvo = new Set([...normalize(flow.question), ...flow.terms.flatMap((t) => normalize(t))]);
    let hits = 0;
    for (const t of tokens) if (alvo.has(t)) hits++;
    const cobertura = hits / tokens.size;
    if (hits >= 2 && cobertura >= 0.5 && (!melhor || hits > melhor.hits)) {
      melhor = { flow, hits };
    }
  }
  return melhor?.flow;
}

export function composeFlowStep(flow: HelpFlow, stepIndex: number): FlowBlock {
  const passo = flow.steps[stepIndex];
  return {
    kind: "flow",
    flowId: flow.id,
    title: flow.title,
    stepIndex,
    totalSteps: flow.steps.length,
    text: passo.text,
    route: passo.route,
    docId: passo.docId,
  };
}
